import type { BaseDevice } from '../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import { KeyTravelFeature } from '../../../features/key-travel/contract'
import type {
  keyboardCalibration,
  KeyTravelList,
  protocolCailbrationType,
} from '../../../types/keyTravel'
import type { CH315KeyboardProtocol } from '../protocols'
import { CH315_FEATURE_LAYOUT, getCH315ConfigBase } from '../types/feature-types'
import { CH315FeatureMemory, readUint16LE } from './memory'

export class CH315KeyTravel extends KeyTravelFeature {
  private readonly memory: CH315FeatureMemory

  constructor(
    device: BaseDevice,
    private readonly protocol: CH315KeyboardProtocol,
  ) {
    super(device)
    this.memory = new CH315FeatureMemory(protocol)
  }

  async getGlobalRTConfig(): Promise<number> {
    return 3
  }

  async setGlobalRTConfig(index: number): Promise<void> {
    if (index !== 3) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'CH315 only exposes per-key RT configuration index 3',
      )
    }
  }

  async getKeyTravel(rt: number): Promise<KeyTravelList> {
    if (rt !== 3) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'CH315 has no global RT preset; only index 3 is readable',
      )
    }
    const base = getCH315ConfigBase(this.device.currentConfig.index)
    const [bitmap, trigger, reset, absolute, bottom] = await Promise.all([
      this.memory.read(base + CH315_FEATURE_LAYOUT.rtBitmapOffset, 12),
      this.memory.read(base + CH315_FEATURE_LAYOUT.rtTriggerOffset, 192),
      this.memory.read(base + CH315_FEATURE_LAYOUT.rtResetOffset, 192),
      this.memory.read(base + CH315_FEATURE_LAYOUT.absoluteTriggerOffset, 192),
      this.memory.read(base + CH315_FEATURE_LAYOUT.bottomDeadZoneOffset, 192),
    ])
    return decodeCH315KeyTravelBlock(bitmap, trigger, reset, absolute, bottom)
  }

  async *getKeyTravelStream(rt: number, batchSize = 24): AsyncIterable<KeyTravelList> {
    if (!Number.isInteger(batchSize) || batchSize <= 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'batchSize must be a positive integer')
    }
    const all = await this.getKeyTravel(rt)
    for (let offset = 0; offset < all.length; offset += batchSize) {
      yield all.slice(offset, offset + batchSize)
    }
  }

  async switchCalibrationMode(
    state: boolean,
    options: keyboardCalibration = { ati: false, smd: false, user: false },
  ): Promise<void> {
    if (state) {
      await this.protocol.enterCalibration({ user: options.user, singleKey: false })
    } else {
      await this.protocol.exitCalibration()
    }
  }

  async saveCalibration(_type: protocolCailbrationType): Promise<void> {
    await this.protocol.saveCalibration()
  }

  // switchDemoMode/calibration/switchAdaptiveCalibration and all travel writes remain
  // NOT_SUPPORTED: their public semantics cannot be represented safely by B0/B1/B2 or
  // by a partial write to the Checksum32-protected config block.
}

/** 将协议 0.001 mm 单位转换为公共 KeyTravel 的 0.01 mm 单位。 */
export function protocolTravelToPublic(value: number): number {
  return Math.round(value / 10)
}

export function decodeCH315KeyTravelBlock(
  bitmap: Uint8Array,
  trigger: Uint8Array,
  reset: Uint8Array,
  absolute: Uint8Array,
  bottom: Uint8Array,
): KeyTravelList {
  if (
    bitmap.byteLength < 12 ||
    trigger.byteLength < 192 ||
    reset.byteLength < 192 ||
    absolute.byteLength < 192 ||
    bottom.byteLength < 192
  ) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, 'CH315 key-travel block is truncated')
  }

  return Array.from({ length: CH315_FEATURE_LAYOUT.keyCount }, (_, index) => ({
    index,
    keyTravel: {
      absoluteTrigger: protocolTravelToPublic(readUint16LE(absolute, index * 2)),
      rtTriggerSensitivity: protocolTravelToPublic(readUint16LE(trigger, index * 2)),
      rtResetSensitivity: protocolTravelToPublic(readUint16LE(reset, index * 2)),
      rtTriggerEnable: Boolean(bitmap[index >>> 3] & (1 << (index & 7))),
      absoluteTriggerEnable: true,
      bottomDeadZone: protocolTravelToPublic(readUint16LE(bottom, index * 2)),
    },
  }))
}
